/**
 * BundleBuilder-local RHF fork of CommonFields/BBTierDiscounts.tsx (shared
 * with Placeholder via PlaceholderDiscount.tsx, so not editable in place).
 * `discount_options` is still always WRITTEN as a whole array (every edit
 * goes through useFieldArray's `replace()`, matching the pre-migration
 * behavior), but each input is now addressed via FormFields with its real
 * array-indexed RHF path (`discount_options.${index}.field`) — same pattern
 * Volume uses for its own per-tier `volume_discounts.${index}.field` rows —
 * so AB-variant auto-disable works per field.
 * The per-tier validation error list stays local component state driven by
 * `validateBBTierDiscounts`, exactly like the pre-migration version — it was
 * never wired to the parent form's validation, only to inline field errors,
 * now surfaced via FormFields' `error` override.
 */
import { useState, useContext, useEffect } from "react";
import styles from "../../CommonFields/CommonFields.module.css";
import { FormLayout, Icon, Card, Tooltip, BlockStack, Text } from "@shopify/polaris";
import { DISCOUNT_TYPES, generateUniqueId, getDiscountOptions, validateBBTierDiscounts } from "../../constants";
import { buildResourcePickerQuery, extractNumericIds } from "../../../constants";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { BBTierOptions, DiscountErrorInterface } from "../../Types";
import { AlertTriangleIcon, DeleteIcon, PlusIcon } from "@shopify/polaris-icons";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import { getNumberFieldSuffix } from "../../../../utilities/constants";
import { PiBracketsCurlyBold } from "react-icons/pi";
import RHFRenderSelectedProduct from "../../../../components/RHFRenderSelectedProduct/RHFRenderSelectedProduct";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { BundleTypes } from "../../constants";
import { FormCheckbox, FormRadioButton, FormSelect, FormTextField } from "../../../../components/FormFields";

const TierCard = ({
    option,
    index,
    handleDeleteOption,
    handleDiscountInputChange,
    errors,
    discountOptionsFieldErrors,
    money_format,
    tierDiscountBasis,
    discountOptions,
}: {
    option: BBTierOptions;
    index: number;
    handleDeleteOption: (index: number) => void;
    handleDiscountInputChange: (index: number, field: keyof BBTierOptions, value: any) => void;
    errors: DiscountErrorInterface[];
    discountOptionsFieldErrors?: Record<string, any>;
    money_format: string;
    tierDiscountBasis: string;
    discountOptions: BBTierOptions[];
}) => {
    useEffect(() => {
        if (option.discountType === DISCOUNT_TYPES.FIXED && option.addRange) {
            handleDiscountInputChange(index, "addRange", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [option.discountType]);

    return (
        <Card background="bg-surface-secondary">
            <div key={option.id} className={styles.optionContainer}>
                <div className={styles.optionHeader}>
                    <label className={styles.optionLabel}>OPTION {index + 1}</label>
                    <span className={styles.deleteIcon} onClick={() => handleDeleteOption(index)}>
                        <Icon source={DeleteIcon} tone="base" />
                    </span>
                </div>

                {tierDiscountBasis === "product_based" && (
                    <div style={{ marginTop: "10px" }}>
                        <RHFRenderSelectedProduct
                            bundleLevel="product"
                            bundleType={BundleTypes.BUNDLE_BUILDER}
                            updateKey={`discount_options.${index}.selected_products`}
                            multiple={true}
                            customTitle="Select Products"
                            resourcePickerQuery={buildResourcePickerQuery({
                                products: extractNumericIds(
                                    discountOptions
                                        ?.filter((_: any, i: number) => i !== index)
                                        .flatMap((opt: any) => opt.selected_products || []),
                                ),
                                filters: ["exclude"],
                                includeUnlisted: true,
                            })}
                            value={option.selected_products || []}
                            errorMessage={
                                discountOptionsFieldErrors?.[index]?.selected_products?.message ||
                                errors?.[index]?.selected_products ||
                                undefined
                            }
                            onChange={(products: any) => {
                                handleDiscountInputChange(index, "selected_products", products);
                            }}
                        />
                    </div>
                )}

                <div
                    className={tierDiscountBasis === "product_based" ? styles.formRow : ""}
                    style={{ ...(tierDiscountBasis === "product_based" ? { alignItems: "center" } : {}) }}
                >
                    <div style={{ margin: "10px 0" }}>
                        <FormSelect
                            name={`discount_options.${index}.discountType`}
                            label="Discount Type"
                            options={getDiscountOptions(money_format.split("{{")[0].trim()).filter((op) => {
                                if (tierDiscountBasis === "product_based") {
                                    return op.value === DISCOUNT_TYPES.PERCENTAGE || op.value === DISCOUNT_TYPES.FLAT;
                                }
                                return op.value != "none";
                            })}
                            onChange={(val) => handleDiscountInputChange(index, "discountType", val)}
                            value={option.discountType}
                        />
                    </div>

                    <div
                        className={
                            tierDiscountBasis !== "product_based"
                                ? `${styles.formRow} ${option.discountType === DISCOUNT_TYPES.FIXED ? "" : styles.row4}`
                                : ""
                        }
                    >
                        {option.discountType !== DISCOUNT_TYPES.FIXED && tierDiscountBasis !== "product_based" && (
                            <FormSelect
                                name={`discount_options.${index}.addRange`}
                                label="Tier Discounts On"
                                options={[
                                    {
                                        value: "minimum",
                                        label: `Minimum ${tierDiscountBasis == "bundle_total" ? "amount" : "products"} for this tier`,
                                    },
                                    {
                                        value: "exact",
                                        label: `Exact ${tierDiscountBasis == "bundle_total" ? "amount" : "products"} for this tier`,
                                    },
                                ]}
                                onChange={(val) => handleDiscountInputChange(index, "addRange", val === "minimum")}
                                value={option?.addRange ? "minimum" : "exact"}
                            />
                        )}

                        {tierDiscountBasis !== "product_based" && (
                            <FormTextField
                                name={`discount_options.${index}.quantity`}
                                label={tierDiscountBasis == "items_count" ? "Quantity" : "Total"}
                                type="number"
                                placeholder="0"
                                error={errors[index]?.quantity}
                                suffix={
                                    tierDiscountBasis == "bundle_total"
                                        ? getNumberFieldSuffix("fixed", money_format).suffix
                                        : ""
                                }
                                value={option.quantity?.toString()}
                                onBlur={() => {
                                    handleDiscountInputChange(
                                        index,
                                        "quantity",
                                        Number(option.quantity) <= 0 ? 1 : Number(option.quantity),
                                    );
                                }}
                                onChange={(val) => handleDiscountInputChange(index, "quantity", +val)}
                            />
                        )}

                        <FormTextField
                            name={`discount_options.${index}.discountValue`}
                            label={
                                option?.discountType === DISCOUNT_TYPES.PERCENTAGE
                                    ? "% Discount Off"
                                    : option?.discountType === DISCOUNT_TYPES.FLAT
                                      ? "Fixed Amount Off"
                                      : option?.discountType === DISCOUNT_TYPES.FIXED
                                        ? "Fixed Price"
                                        : "Discount Value"
                            }
                            type="number"
                            placeholder="Enter value"
                            error={errors[index]?.discountValue}
                            suffix={getNumberFieldSuffix(option.discountType, money_format).suffix}
                            value={option.discountValue?.toString()}
                            onBlur={() => {
                                let val: string | number = option.discountValue || 0;
                                const regex = /^(\d+(\.\d{0,2})?)?$/;
                                if (regex.test(String(val))) {
                                    if (option.discountType === "percentage" && +val > 100) val = "100";
                                    handleDiscountInputChange(index, "discountValue", +val || 0);
                                }
                            }}
                            onChange={(val) => {
                                const regex = /^(\d+(\.\d{0,2})?)?$/;
                                if (regex.test(val)) {
                                    if (option.discountType === "percentage" && +val > 100) val = "100";
                                    handleDiscountInputChange(index, "discountValue", +val);
                                }
                            }}
                        />
                    </div>
                </div>

                {tierDiscountBasis !== "product_based" && (
                    <>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 2,
                                width: "100%",
                                marginTop: 10,
                            }}
                        >
                            <FormCheckbox
                                label="Message Before Tier Completion"
                                name={`discount_options.${index}.ShowTierSuccessMessage`}
                                checked={option.ShowTierSuccessMessage}
                                onChange={(val) => handleDiscountInputChange(index, "ShowTierSuccessMessage", val)}
                            />
                            <Tooltip
                                width="wide"
                                content={
                                    <>
                                        {tierDiscountBasis == "items_count" && (
                                            <div>{`{noOfRemainingProducts} – Number of additional products required to unlock the discount.`}</div>
                                        )}
                                        {tierDiscountBasis == "bundle_total" && (
                                            <div>{`{remainingAmount} - Amount remaining to unlock the discount.`}</div>
                                        )}
                                        <div>{`{discount} – The discount amount and type you’ll receive (e.g., 10% off, ₹100 off).`}</div>
                                    </>
                                }
                            >
                                <PiBracketsCurlyBold />
                            </Tooltip>
                        </div>
                        <FormTextField
                            name={`discount_options.${index}.TierSuccessMessage`}
                            label=""
                            disabled={!option.ShowTierSuccessMessage}
                            placeholder="Add {noOfRemainingProducts} more product to get {discount} discount!"
                            helpText=""
                            value={option?.TierSuccessMessage}
                            onChange={(val) => handleDiscountInputChange(index, "TierSuccessMessage", val)}
                            maxLength={300}
                        />
                    </>
                )}
            </div>
        </Card>
    );
};

const BBTierDiscounts = () => {
    const { control, formState } = useFormContext();
    const discountOptionsFieldErrors = (formState.errors as any)?.discount_options as Record<string, any> | undefined;
    const { fields: discountOptionFields, replace } = useFieldArray({ control, name: "discount_options" });
    const discountOptions = useWatch({ control, name: "discount_options" }) as BBTierOptions[];
    const tierDiscountBasis = useWatch({ control, name: "tier_discount_basis" }) as
        | "items_count"
        | "bundle_total"
        | "product_based";
    const showProgressBar = useWatch({ control, name: "show_progress_bar" });
    const products = useWatch({ control, name: "products" });

    const appConfigContext = useContext(AppConfigContext);
    const [errors, setErrors] = useState<DiscountErrorInterface[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const { money_format } = appConfigContext;
    const { trackCtaClick } = useBundleMixpanelHook();
    const { isMobile } = useIsMobile();

    const handleUpdateDiscountOptions = (updated: BBTierOptions[]) => {
        replace(updated);
    };

    const handleTierBasisChange = (val: "bundle_total" | "items_count" | "product_based") => {
        let discounts = [...discountOptions];
        discounts = discounts?.map((opt) => {
            let updatedMsg = "";
            if (opt.discountType === DISCOUNT_TYPES.FIXED) {
                updatedMsg =
                    val != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!"
                        : "Add products worth {remainingAmount} more to unlock the fixed price offer of {discount}!";
            } else {
                updatedMsg =
                    val != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to get {discount} discount!"
                        : "Add products worth {remainingAmount} more to get {discount} discount!";
            }
            if (val === "product_based") updatedMsg = "";

            if (opt.TierSuccessMessage !== updatedMsg) {
                const updatedOpt: BBTierOptions = { ...opt, TierSuccessMessage: updatedMsg };
                if (val === "product_based" && opt.discountType === DISCOUNT_TYPES.FIXED) {
                    updatedOpt.discountType = DISCOUNT_TYPES.PERCENTAGE;
                }
                return updatedOpt;
            }
            return opt;
        });
        handleUpdateDiscountOptions(discounts);
    };

    const handleDiscountInputChange = (
        index: number,
        field: keyof BBTierOptions,
        value: string | number | boolean | null,
    ) => {
        const updatedDiscountOptions = [...discountOptions];
        if (field && index >= 0 && updatedDiscountOptions[index] && value != null) {
            const updatedItem = { ...updatedDiscountOptions[index], [field]: value };
            if (field === "quantity" && typeof value === "number") {
                updatedItem.maxQuantity = value;
            }
            if (field == "showFinalSuccessMessage" && value && !updatedItem?.finalSuccessMessage?.length) {
                updatedItem.finalSuccessMessage = "Congratulations! You've got the best offer on your bundle.";
            }
            if (field == "discountType") {
                if (value === DISCOUNT_TYPES.FIXED) {
                    updatedItem.TierSuccessMessage =
                        tierDiscountBasis != "bundle_total"
                            ? "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!"
                            : "Add products worth {remainingAmount} more to unlock the fixed price offer of {discount}!";
                } else if (tierDiscountBasis == "product_based") {
                    updatedItem.TierSuccessMessage = "";
                } else {
                    updatedItem.TierSuccessMessage =
                        tierDiscountBasis != "bundle_total"
                            ? "Add {noOfRemainingProducts} more product(s) to get {discount} discount!"
                            : "Add products worth {remainingAmount} more to get {discount} discount!";
                }
            }
            updatedDiscountOptions[index] = updatedItem;
        }
        validateBBTierDiscounts(updatedDiscountOptions, setErrors, tierDiscountBasis);
        handleUpdateDiscountOptions(updatedDiscountOptions);
    };

    const handleAddOption = () => {
        if (discountOptions?.length < 6) {
            const updatedDiscountOptions: BBTierOptions[] = [...discountOptions];
            const lastOption = updatedDiscountOptions[updatedDiscountOptions.length - 1];
            let successMessage = "";
            if (lastOption.discountType === DISCOUNT_TYPES.FIXED) {
                successMessage =
                    tierDiscountBasis != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!"
                        : "Add products worth {remainingAmount} more to unlock the fixed price offer of {discount}!";
            } else if (tierDiscountBasis == "product_based") {
                successMessage = "";
            } else {
                successMessage =
                    tierDiscountBasis != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to get {discount} discount!"
                        : "Add products worth {remainingAmount} more to get {discount} discount!";
            }
            updatedDiscountOptions.push({
                id: generateUniqueId(),
                discountValue:
                    lastOption.discountType == "percentage" && +lastOption.discountValue == 100
                        ? 100
                        : +lastOption?.discountValue + 5,
                quantity: +lastOption.quantity + 1,
                discountType: lastOption.discountType,
                maxQuantity: +lastOption.quantity + 1,
                addRange: true,
                ShowTierSuccessMessage: tierDiscountBasis !== "product_based",
                TierSuccessMessage: successMessage,
                selected_products: [],
            });

            trackCtaClick({
                cta: "Discount option CTA",
                ctaType: "discount option added",
                bundleType: BundleTypes.BUNDLE_BUILDER,
            });
            validateBBTierDiscounts(updatedDiscountOptions, setErrors, tierDiscountBasis);
            handleUpdateDiscountOptions(updatedDiscountOptions);
            setSelectedTab(discountOptions.length);
        }
    };

    const handleDeleteOption = (index: number) => {
        if (discountOptions?.length === 1) {
            shopify.toast.show("At least one option is necessary", { isError: true });
            return;
        }
        trackCtaClick({
            cta: "Discount option CTA",
            ctaType: "discount option deleted",
            bundleType: BundleTypes.BUNDLE_BUILDER,
        });
        const updatedOptions = discountOptions.filter((_: any, i: number) => i !== index);
        validateBBTierDiscounts(updatedOptions, setErrors, tierDiscountBasis);
        handleUpdateDiscountOptions(updatedOptions);
        setSelectedTab(0);
    };

    const workOnBlur = () => {
        const updatedDiscountOptions = discountOptions.map((element) => {
            const updated = { ...element };
            if (updated.discountType == "percentage" && updated.discountValue > 100) updated.discountValue = 100;
            if (typeof updated.discountValue == "string") updated.discountValue = Number(updated.discountValue) || 0;
            if (typeof updated.quantity == "string") updated.quantity = Number(updated.quantity) || 1;
            if (typeof updated.maxQuantity == "string") updated.maxQuantity = Number(updated.maxQuantity) || 1;
            if (updated.discountValue < 0) updated.discountValue = 1;
            if (updated.discountType === "none") updated.discountValue = 1;
            return updated;
        });
        validateBBTierDiscounts(updatedDiscountOptions, setErrors, tierDiscountBasis);
        handleUpdateDiscountOptions(updatedDiscountOptions);
    };

    const handleDragEnd = (result: any) => {
        if (!result.destination) return;
        const reorderedOffers: BBTierOptions[] = Array.from(discountOptions);
        const [movedOffer] = reorderedOffers.splice(result.source.index, 1);
        reorderedOffers.splice(result.destination.index, 0, movedOffer);
        const reorderedErrors = Array.from(errors);
        const [movedError] = reorderedErrors.splice(result.source.index, 1);
        reorderedErrors.splice(result.destination.index, 0, movedError);
        setErrors(reorderedErrors);
        validateBBTierDiscounts(reorderedOffers, setErrors, tierDiscountBasis);
        handleUpdateDiscountOptions(reorderedOffers);
        setSelectedTab(result.destination.index);
    };

    useEffect(() => {
        workOnBlur();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    if (!discountOptions?.length) return null;

    return (
        <FormLayout>
            <div className={styles.container}>
                <BlockStack gap="200">
                    <Text as="h3" variant="headingSm" fontWeight="bold">
                        2. Discount Logic
                    </Text>
                    <Text as="p" variant="bodyMd">
                        Apply these levels based on:
                    </Text>
                    <div
                        style={{
                            display: "flex",
                            gap: isMobile ? "5px" : "20px",
                            flexDirection: isMobile ? "column" : "row",
                        }}
                    >
                        <FormRadioButton
                            label="Number of Items added"
                            id="items_count"
                            name="tier_discount_basis"
                            value="items_count"
                            onChangeHook={() => handleTierBasisChange("items_count")}
                        />
                        <FormRadioButton
                            label="Total Cart Amount"
                            id="bundle_total"
                            name="tier_discount_basis"
                            value="bundle_total"
                            onChangeHook={() => handleTierBasisChange("bundle_total")}
                        />
                        <FormRadioButton
                            label="Product Based"
                            id="product_based"
                            name="tier_discount_basis"
                            value="product_based"
                            onChangeHook={() => handleTierBasisChange("product_based")}
                        />
                    </div>
                </BlockStack>

                <div className={styles.offerTabContainer}>
                    {
                        // @ts-expect-error required
                        <DragDropContext onDragEnd={handleDragEnd}>
                            {
                                // @ts-expect-error required
                                <Droppable droppableId="droppable-tabs" direction="horizontal">
                                    {(provided) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className={styles.offerTabDroppableContainer}
                                            style={{ borderBottom: "1px solid #E1E3E5" }}
                                        >
                                            {discountOptionFields.map((field, index) => (
                                                // @ts-expect-error required
                                                <Draggable key={field.id} draggableId={field.id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            onClick={() => setSelectedTab(index)}
                                                            className={`${styles.offerTabDraggableItem} ${snapshot.isDragging ? styles.offerTabDraggableItemDragging : ""}`}
                                                            style={{ padding: "0px" }}
                                                        >
                                                            <span
                                                                style={{
                                                                    padding: "8px 16px",
                                                                    backgroundColor:
                                                                        selectedTab === index
                                                                            ? "#f4f6f8"
                                                                            : "transparent",
                                                                    color:
                                                                        selectedTab === index ? "#202223" : "#6D7175",
                                                                    cursor: "pointer",
                                                                    whiteSpace: "nowrap",
                                                                    fontWeight:
                                                                        selectedTab === index ? "bold" : "normal",
                                                                    fontSize: "14px",
                                                                    border: "1px solid transparent",
                                                                    borderBottom:
                                                                        selectedTab === index
                                                                            ? "none"
                                                                            : "1px solid transparent",
                                                                    borderRadius: "8px 8px 0 0",
                                                                    ...(selectedTab === index && {
                                                                        border: "1px solid #E1E3E5",
                                                                        borderBottom: "1px solid white",
                                                                        marginBottom: "-1px",
                                                                    }),
                                                                }}
                                                            >
                                                                {tierDiscountBasis === "product_based"
                                                                    ? `Group ${index + 1}`
                                                                    : `Tier ${index + 1}`}{" "}
                                                                {errors[index]?.quantity && (
                                                                    <Icon source={AlertTriangleIcon} tone="warning" />
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder as any}
                                            {discountOptions.length < 6 && (
                                                <button
                                                    style={{
                                                        padding: "8px 16px",
                                                        backgroundColor: "transparent",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                        color: "#005BD3",
                                                        fontSize: "14px",
                                                        fontWeight: "500",
                                                    }}
                                                    onClick={handleAddOption}
                                                >
                                                    <Icon source={PlusIcon} tone="interactive" />
                                                    {tierDiscountBasis === "product_based" ? "Add Group" : "Add Tier"}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            }
                        </DragDropContext>
                    }
                </div>

                {discountOptions[selectedTab] && (
                    <TierCard
                        key={discountOptions[selectedTab]?.id}
                        option={discountOptions[selectedTab]}
                        index={selectedTab}
                        handleDeleteOption={handleDeleteOption}
                        errors={errors}
                        discountOptionsFieldErrors={discountOptionsFieldErrors}
                        money_format={money_format}
                        handleDiscountInputChange={handleDiscountInputChange}
                        tierDiscountBasis={tierDiscountBasis}
                        discountOptions={discountOptions}
                    />
                )}

                {tierDiscountBasis !== "product_based" && (
                    <BlockStack gap="200">
                        <Text as="h3" variant="headingSm" fontWeight="bold">
                            3. Final Completion Message
                        </Text>
                        <Card padding="400" background="bg-surface-secondary">
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <FormCheckbox
                                    label="Show Progress Bar"
                                    name="show_progress_bar"
                                    checked={showProgressBar}
                                />
                                <FormCheckbox
                                    label="Success Message After All Tier Completion"
                                    name="discount_options.0.showFinalSuccessMessage"
                                    checked={discountOptions?.[0]?.showFinalSuccessMessage}
                                    onChange={(val) => handleDiscountInputChange(0, "showFinalSuccessMessage", val)}
                                />
                            </div>
                            <FormTextField
                                name="discount_options.0.finalSuccessMessage"
                                label=""
                                disabled={!discountOptions?.[0]?.showFinalSuccessMessage}
                                placeholder="Congratulations! You've got the best offer on your bundle."
                                value={discountOptions?.[0]?.finalSuccessMessage}
                                onChange={(val) => handleDiscountInputChange(0, "finalSuccessMessage", val)}
                                maxLength={300}
                            />
                        </Card>
                    </BlockStack>
                )}
            </div>
        </FormLayout>
    );
};

export default BBTierDiscounts;
